import { COMPANY_PDF_LOGO } from "@/lib/brand";
import { formatDate, formatNumber } from "@/lib/format";

// Mirrors the printable shape used by the old BillForge desktop app so the
// rendered output is pixel-identical to what users were used to. The form
// pages map their internal state into this type before mounting the print
// component off-screen for html2canvas → jsPDF capture.
export interface QuotationPrintItem {
  productName: string;
  unit: string;
  quantity: number;
  rate: number;
  amount: number;
}

export interface QuotationPrintData {
  number: string;
  pfiDate?: string | number | null;
  projectType?: string;
  customerName: string;
  telephone?: string;
  location?: string;
  registered?: boolean;
  // Optional Entity contacts. When non-empty they render as additional
  // labelled rows in the customer block on the PDF. Empty / undefined
  // values are skipped entirely (no "Estimator: —" placeholder).
  estimatorName?: string;
  attentionName?: string;
  // Required quotation-level attributes (Gauge / Profile / Color). New
  // quotations always carry a value but quotes saved BEFORE migration
  // 0019 have null/empty — the print block renders "—" in that case so
  // the layout doesn't crash on legacy rows.
  gauge?: string | null;
  profile?: string | null;
  color?: string | null;
  items: QuotationPrintItem[];
  subtotal: number;
  discount: number;
  // When false (deployment's `features.tax` flag is OFF) the Taxable Amount /
  // NHIL / GETFund / VAT rows are omitted and NET TOTAL collapses to the
  // taxable (post-discount) figure. Defaults to true so existing callers and
  // tax-bearing documents render exactly as before.
  showTax?: boolean;
  taxable: number;
  nhilRate: number;
  nhil: number;
  getfundRate: number;
  getfund: number;
  vatRate: number;
  vat: number;
  transportation?: number;
  installationRate?: number;
  installationAmount?: number;
  total: number;
  notes?: string;
}

export interface QuotationPrintCompany {
  name: string;
  address: string;
  phone: string;
  email: string;
  currency: string;
}

interface Props {
  q: QuotationPrintData;
  company: QuotationPrintCompany;
  isReceipt?: boolean;
  termsText?: string;
  preparedBy?: string;
}

export default function QuotationPrint({
  q,
  company,
  isReceipt = false,
  termsText = "",
  preparedBy = "System",
}: Props) {
  const terms: string = String(termsText || "");
  // The amount is already computed in the form (Subtotal × installationRate
  // when a percent is supplied, or the user's direct figure otherwise) so
  // the print view just shows it verbatim and uses the rate purely for the
  // label suffix.
  const installAmt = Number(q.installationAmount || 0);
  const hasOptional = (q.transportation || 0) > 0 || installAmt > 0;
  // Right-hand meta column. Location / Gauge / Profile / Color are pushed
  // here so they line up underneath the PFI Date (top-right). Only rendered
  // when they actually carry a value — no empty "—" stubs in this column.
  const gauge = q.gauge && q.gauge.trim() ? q.gauge.trim() : "";
  const profile = q.profile && q.profile.trim() ? q.profile.trim() : "";
  const color = q.color && q.color.trim() ? q.color.trim() : "";
  const location = q.location && q.location.trim() ? q.location.trim() : "";
  const telephone = q.telephone && q.telephone.trim() ? q.telephone.trim() : "";
  const estimator =
    q.estimatorName && q.estimatorName.trim() ? q.estimatorName.trim() : "";
  const attention =
    q.attentionName && q.attentionName.trim() ? q.attentionName.trim() : "";
  return (
    <div
      className="print-area relative bg-white text-slate-900 p-5 font-sans overflow-hidden flex flex-col min-h-[1131px]"
      style={{ width: "800px" }}
    >
      <div className="relative z-10 flex flex-col flex-1">
        <div className="flex justify-between items-start border-b-2 border-slate-300 pb-2">
          <div className="flex items-center gap-4">
            <img
              src={COMPANY_PDF_LOGO}
              alt="Company logo"
              className="h-20 w-auto object-contain"
            />
            <div>
              <div className="text-lg font-bold uppercase leading-tight">
                {company.name}
              </div>
              <div className="text-[11px] mt-0.5">{company.address}</div>
              <div className="text-[11px]">Tel: {company.phone}</div>
              <div className="text-[11px]">Email: {company.email}</div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-xl font-bold tracking-wide leading-tight">
              {isReceipt ? "RECEIPT" : "QUOTATION"}
            </div>
            <div className="text-sm font-mono mt-0.5">{q.number}</div>
            {q.projectType ? (
              <div className="text-[11px] mt-1">
                Project Type:{" "}
                <span className="font-medium">{q.projectType}</span>
              </div>
            ) : null}
          </div>
        </div>

        {/*
          Two-column meta header:
          - Left  → customer / entity identity (Customer, Telephone,
                    Estimator, Attention).
          - Right → PFI Date + Location + Gauge + Profile + Color, all
                    right-aligned so they sit directly under the Date.
          Profile is kept alongside Gauge / Color because the three are
          sibling quotation attributes and read naturally grouped.
        */}
        <div className="mt-3 flex justify-between gap-6 text-[13px] leading-tight">
          <div className="space-y-0.5">
            <MetaRow label="Customer Name" value={q.customerName} />
            {telephone ? <MetaRow label="Telephone" value={telephone} /> : null}
            {estimator ? <MetaRow label="Estimator" value={estimator} /> : null}
            {attention ? <MetaRow label="Attention" value={attention} /> : null}
          </div>
          <div className="space-y-0.5 text-right">
            <MetaRow
              label="PFI Date"
              value={q.pfiDate ? formatDate(q.pfiDate) : "—"}
            />
            {location ? <MetaRow label="Location" value={location} /> : null}
            {gauge ? <MetaRow label="Gauge" value={gauge} /> : null}
            {profile ? <MetaRow label="Profile" value={profile} /> : null}
            {color ? <MetaRow label="Color" value={color} /> : null}
          </div>
        </div>

        <table className="w-full mt-3 text-[11px] border-collapse">
          <thead>
            <tr className="bg-slate-100">
              <th className="border border-slate-300 px-2 py-1.5 text-center w-8">
                #
              </th>
              <th className="border border-slate-300 px-2 py-1.5 text-left">
                Product Name
              </th>
              <th className="border border-slate-300 px-2 py-1.5 text-left">
                Unit
              </th>
              <th className="border border-slate-300 px-2 py-1.5 text-right">
                Quantity
              </th>
              <th className="border border-slate-300 px-2 py-1.5 text-right">
                Rate ({company.currency})
              </th>
              <th className="border border-slate-300 px-2 py-1.5 text-right">
                Amount ({company.currency})
              </th>
            </tr>
          </thead>
          <tbody>
            {q.items.map((it, i) => (
              <tr key={i}>
                <td className="border border-slate-300 px-2 py-1.5 text-center align-middle">
                  {i + 1}
                </td>
                <td className="border border-slate-300 px-2 py-1.5 align-middle">
                  <div>{it.productName}</div>
                </td>
                <td className="border border-slate-300 px-2 py-1.5 align-middle">
                  {it.unit}
                </td>
                <td className="border border-slate-300 px-2 py-1.5 text-right align-middle">
                  {formatNumber(it.quantity)}
                </td>
                <td className="border border-slate-300 px-2 py-1.5 text-right font-mono align-middle">
                  {formatNumber(it.rate)}
                </td>
                <td className="border border-slate-300 px-2 py-1.5 text-right font-mono align-middle">
                  {formatNumber(it.amount)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="mt-2 flex justify-end">
          <table className="text-[11px] w-72">
            <tbody>
              <Row label="Subtotal" value={q.subtotal} />
              <Row
                label={`Discount (${q.discount}%)`}
                value={(q.subtotal * (q.discount || 0)) / 100}
              />
              {q.showTax !== false && (
                <>
                  <Row label="Taxable Amount" value={q.taxable} />
                  <Row label={`NHIL (${q.nhilRate}%)`} value={q.nhil} />
                  <Row label={`GETFund (${q.getfundRate}%)`} value={q.getfund} />
                  <Row label={`VAT (${q.vatRate}%)`} value={q.vat} />
                </>
              )}
              <tr className="border-t border-slate-300">
                <td className="py-0.5 font-semibold">NET TOTAL</td>
                <td className="py-0.5 text-right font-mono font-semibold">
                  {formatNumber(
                    q.showTax === false
                      ? q.taxable || 0
                      : (q.taxable || 0) +
                          (q.nhil || 0) +
                          (q.getfund || 0) +
                          (q.vat || 0),
                  )}
                </td>
              </tr>
              {hasOptional && (
                <tr>
                  <td
                    colSpan={2}
                    className="pt-1 text-[10px] uppercase tracking-wider text-slate-500"
                  >
                    Optional Services
                  </td>
                </tr>
              )}
              {(q.transportation || 0) > 0 && (
                <Row label="Transportation" value={q.transportation || 0} />
              )}
              {installAmt > 0 && (
                <Row
                  label={`Installation${
                    q.installationRate ? ` (${q.installationRate}%)` : ""
                  }`}
                  value={installAmt}
                />
              )}
              <tr className="border-t-2 border-slate-400">
                <td className="py-1 font-bold">Total ({company.currency})</td>
                <td className="py-1 text-right font-bold text-sm">
                  {formatNumber(q.total)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="mt-auto pt-2 border-t border-slate-200 text-xs">
          <div>Thank you for your business!</div>
          {q.notes && (
            <div className="mt-1 text-[10px] text-slate-600">
              Notes: {q.notes}
            </div>
          )}
          {terms.trim() && (
            <div className="mt-2 pt-2 border-t border-slate-200">
              <div className="text-[10px] uppercase font-semibold text-slate-700 mb-0.5">
                Terms &amp; Conditions
              </div>
              <div className="text-[9px] text-slate-600 whitespace-pre-wrap leading-snug">
                {terms}
              </div>
            </div>
          )}
          <div className="mt-3 pt-2 border-t border-slate-200 flex items-end justify-between">
            <div className="text-[11px]">
              <div className="text-slate-500 uppercase tracking-wide">
                Prepared By
              </div>
              <div className="font-semibold mt-0.5">{preparedBy}</div>
            </div>
            <div className="text-[11px] text-right min-w-[220px]">
              <div className="text-slate-500 uppercase tracking-wide">
                Customer Signature
              </div>
              <div className="mt-4 border-b border-slate-400 w-full" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Single labelled meta line. Inherits text alignment from its container so
// the right-hand column renders right-aligned without extra props.
function MetaRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <span className="text-[10px] uppercase text-slate-500">{label}: </span>
      <span className="font-semibold">{value}</span>
    </div>
  );
}

function Row({ label, value }: { label: string; value: number }) {
  return (
    <tr>
      <td className="py-0.5 text-slate-600">{label}</td>
      <td className="py-0.5 text-right font-mono">{formatNumber(value)}</td>
    </tr>
  );
}
